import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useFeature } from '@lib/feature-toggles/useFeature';
import { FeatureFlag } from '@lib/feature-toggles/flags';

export const useAnalytics = () => {
  const router = useRouter();
  const [isInitialized, setIsInitialized] = useState(false);
  const analyticsEnabled = useFeature(FeatureFlag.GoogleAnalytics);

  const initializeAnalytics = (trackingId: string) => {
    if (!isInitialized && analyticsEnabled) {
      ReactGA.initialize(trackingId);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    const setPageView = (url: string) => {
      ReactGA.set({ page: url });
      ReactGA.send({ hitType: 'pageview', page: url, title: document.title });
    };
    if (isInitialized && analyticsEnabled) {
      router.events.on('routeChangeComplete', setPageView);
    }

    return () => {
      router.events.off('routeChangeComplete', setPageView);
    };
  }, [isInitialized, router, analyticsEnabled]);

  const trackPageClick = (url: string) => {
    if (analyticsEnabled) {
      ReactGA.event({
        category: 'Page Click',
        action: 'Clicked',
        label: url,
      });
    }
  };

  const trackUserLogout = () => {
    if (analyticsEnabled) {
      ReactGA.event({
        category: 'User',
        action: 'Logged Out',
      });
    }
  };

  const trackCustomEvent = (
    category: string,
    action: string,
    label?: string
  ) => {
    if (analyticsEnabled) {
      ReactGA.event({
        category,
        action,
        label,
      });
    }
  };

  return {
    initializeAnalytics,
    trackCustomEvent,
    trackUserLogout,
    trackPageClick,
  };
};
